import {
  AppCard,
  Button,
  FormAsyncAutocomplete,
  FormTextField,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { Box, CardContent, CircularProgress } from "@mui/material";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { User } from "../../../../../@types/services/User";
import { useUpdateQuotationBatch } from "../../../../../__generated__/orval/endpoints/quotation-batches/quotation-batches";
import { listQuotations } from "../../../../../__generated__/orval/endpoints/quotations/quotations";
import { ForbiddenError } from "../../../../../errors/ForbiddenError";
import { fetchUsers } from "../../../../../services/users";
import { buildListQuotationsParams } from "../../quotations/-queries";
import type { QuotationListItem } from "../-internal_components/PrList";
import { PrList } from "../-internal_components/PrList";
import { quotationBatchDetailQueryOptions } from "../-queries";

const searchSchema = z.object({
  quotation_ids: z.optional(z.array(z.coerce.number())).catch(undefined),
});

interface EditFormValues {
  name: string;
  responsible: {
    id: number;
    login: string;
    full_name: string;
    full_name_with_login: string;
  };
}

const QuotationBatchEditForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { quotationBatchId } = Route.useParams();
  const { quotation_ids } = Route.useSearch();

  const { data: quotationBatch } = useSuspenseQuery(
    quotationBatchDetailQueryOptions(Number(quotationBatchId)),
  );

  const [quotationList, setQuotationList] = useState<QuotationListItem[]>(
    quotationBatch.quotations ?? [],
  );
  const loadedRef = useRef(false);

  useEffect(() => {
    if (quotation_ids && quotation_ids.length > 0 && !loadedRef.current) {
      loadedRef.current = true;

      const loadQuotations = async () => {
        try {
          const data = await listQuotations(
            buildListQuotationsParams({
              filters: {
                id: quotation_ids,
              },
              items_per_page: quotation_ids.length,
            }),
          );

          if (data.results) {
            setQuotationList((prev) => {
              const existingIds = prev.map((q) => q.id);
              const newQuotations = data.results.filter(
                (q) => !existingIds.includes(q.id),
              ) as unknown as QuotationListItem[];
              return [...prev, ...newQuotations];
            });
            enqueueSnackbar(t("quotationBatch.messages.quotationsAdded"), {
              variant: "success",
            });
          }
        } catch {
          enqueueSnackbar(t("quotationBatch.messages.fetchQuotationsError"), {
            variant: "error",
          });
        }
      };
      loadQuotations();
    }
  }, [quotation_ids, t]);

  const { control, getValues } = useForm<EditFormValues>({
    defaultValues: {
      name: quotationBatch.name,
      responsible: quotationBatch.responsible,
    },
  });

  const { mutate: updateBatch, isPending } = useUpdateQuotationBatch({
    mutation: {
      onError: (error: Error) => {
        enqueueSnackbar(
          error.message || t("quotationBatch.messages.updateError"),
          { variant: "error" },
        );
      },
      onSuccess: async () => {
        enqueueSnackbar(t("quotationBatch.messages.updateSuccess"), {
          variant: "success",
        });
        await queryClient.invalidateQueries({
          queryKey: ["quotationBatches"],
        });
        navigate({
          to: "/vbuyer/quotation_batches",
        });
      },
    },
  });

  const handleRemoveQuotation = useCallback(
    (id: number) => {
      if (quotationList.length <= 1) {
        enqueueSnackbar(t("quotationBatch.messages.minimumOneQuotation"), {
          variant: "warning",
        });
        return;
      }
      setQuotationList((prev) => prev.filter((q) => q.id !== id));
    },
    [quotationList.length, t],
  );

  const handleSubmit = useCallback(() => {
    const { name: batchName, responsible: selectedResponsible } = getValues();

    if (!batchName || batchName.trim() === "") {
      enqueueSnackbar(t("quotationBatch.messages.emptyName"), {
        variant: "error",
      });
      return;
    }

    if (quotationList.length === 0) {
      enqueueSnackbar(t("quotationBatch.messages.minimumOneQuotation"), {
        variant: "error",
      });
      return;
    }

    const params: {
      name: string;
      responsible_id?: number;
      quotation_ids?: number[];
    } = {
      name: batchName,
      quotation_ids: quotationList.map((q) => q.id),
    };

    if (selectedResponsible) {
      params.responsible_id = selectedResponsible.id;
    }

    updateBatch({ data: params, id: Number(quotationBatchId) });
  }, [getValues, quotationList, t, updateBatch, quotationBatchId]);

  const handleChooseMore = useCallback(() => {
    navigate({
      search: {
        editQuotationBatchId: Number(quotationBatchId),
        tab: "open",
      },
      to: "/vbuyer/quotations",
    });
  }, [navigate, quotationBatchId]);

  return (
    <PageContainer showBackButton title={t("quotationBatch.edit")}>
      <AppCard>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <FormTextField
              control={control}
              label={t("common.name")}
              name="name"
              required
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormAsyncAutocomplete
              control={control}
              fetchOptions={fetchUsers}
              filters={(searchTerm: string) => ({
                equinor_user: true,
                name_or_login: searchTerm,
              })}
              getOptionLabel={(option: User) =>
                option?.full_name_with_login ?? ""
              }
              isOptionEqualToValue={(option: User, value: string | User) =>
                typeof value !== "string" && option?.id === value?.id
              }
              label={t("common.responsible")}
              minSearchLength={3}
              name="responsible"
              queryKey={["users", "infinite"]}
            />
          </Box>

          <PrList
            onRemove={handleRemoveQuotation}
            quotationList={quotationList}
          />

          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 3 }}
          >
            <Button
              disabled={isPending}
              onClick={handleChooseMore}
              variant="outlined"
            >
              {t("common.chooseMorePrs")}
            </Button>
            <Button
              disabled={isPending}
              onClick={() =>
                navigate({
                  to: "/vbuyer/quotation_batches",
                })
              }
              variant="outlined"
            >
              {t("common.cancel")}
            </Button>
            <Button
              disabled={isPending}
              onClick={handleSubmit}
              variant="contained"
            >
              {isPending ? <CircularProgress size={24} /> : t("common.apply")}
            </Button>
          </Box>
        </CardContent>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/quotation_batches/$quotationBatchId/edit",
  // biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
)({
  validateSearch: searchSchema,
  component: () => (
    <Suspense
      fallback={
        <PageContainer showBackButton={false} title="...">
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        </PageContainer>
      }
    >
      <QuotationBatchEditForm />
    </Suspense>
  ),
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Edit Quotation Batches")) {
      return;
    }

    throw new ForbiddenError();
  },
  loader: ({ context, params }) => {
    context.queryClient.prefetchQuery(
      quotationBatchDetailQueryOptions(Number(params.quotationBatchId)),
    );
    return null;
  },
});
